/**
 * Vorlage „Im Wasserbad schmelzen“.
 *
 * Eine Schüssel sitzt auf einem Topf mit leise siedendem Wasser, ohne es zu
 * berühren. Darin schmelzen Schokoladen- oder Butterstücke unter Rühren zu
 * einer glatten Masse. Seitlich entweicht etwas Dampf.
 */
import { ease, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, rect, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { steam } from '../parts/effects.ts';
import { scatter } from '../parts/food.ts';
import { arcArrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { place, spatula } from '../parts/utensils.ts';
import { bowl, contents, pot } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const POT = { cx: 108, width: 100, height: 40 } as const;
const POT_RIM = GRATE_Y - POT.height;
const BOWL = { cx: 108, rim: 54, width: 132, height: 44 } as const;
const KNOB_X = 214;
const POWER = 0.3;
/** Füllstand in der Schüssel (0 Rand, 1 Boden). */
const LEVEL = 0.42;

/** Zeitplan. */
const TIME = { melt: [0.08, 0.72], reset: [0.9, 1] } as const;

/** Inhalte und ihre Töne: Stücke und geschmolzene Masse. */
const CONTENTS = {
  chocolate: { label: 'Schokolade', chunk: 'deep', melted: 'deep' },
  butter: { label: 'Butter', chunk: 'snow', melted: 'tint' },
} as const satisfies Record<string, { label: string; chunk: Tone; melted: Tone }>;
type Content = keyof typeof CONTENTS;

/** Vorlage „Im Wasserbad schmelzen“. */
export const stoveBainMarieTemplate = defineTemplate({
  id: 'stove.bain-marie',
  group: 'stove',
  title: 'Im Wasserbad schmelzen',
  description:
    'Eine Schüssel sitzt auf einem Topf mit leise siedendem Wasser. Darin schmelzen Schokolade oder Butter unter Rühren langsam zu einer glatten Masse.',
  durationSeconds: 6,
  keywords: ['wasserbad', 'über dem wasserbad', 'schmelzen', 'kuvertüre', 'zerlassen'],
  stillTime: 0.5,
  parameters: [
    choice(
      'content',
      'Inhalt',
      Object.fromEntries(Object.entries(CONTENTS).map(([key, value]) => [key, value.label])),
      'chocolate',
    ),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('butter') && !/schoko|kuvert/.test(lower)) return { content: 'butter' };
    if (/schoko|kuvert/.test(lower)) return { content: 'chocolate' };
    return {};
  },
  scene(values, t) {
    const content = CONTENTS[textValue(values, 'content', 'chocolate') as Content];
    const potVessel = pot(POT.cx, POT_RIM, POT.width, POT.height);
    const bowlVessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const burner = stove(POT.cx, POWER, t);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const melt = ease.inOutSine(segment(t, ...TIME.melt));

    const surfaceY = BOWL.rim + bowlVessel.depth * LEVEL;
    const rx = bowlVessel.widthAt(LEVEL);
    const ry = bowlVessel.ry * (rx / bowlVessel.rx);
    // Stücke schrumpfen nacheinander; darunter wächst die glatte Masse.
    const chunkNodes = (amount: number): SceneNode[] =>
      scatter(BOWL.cx, surfaceY - 2, rx * 0.62, ry * 0.5, 9, 31).map((position, index) => {
        const own = segment(amount, index * 0.05, 0.55 + index * 0.05);
        const size = 12 * (1 - own);
        if (size < 0.8) {
          return group([]);
        }
        return group(
          [
            rect(position.x - size / 2, position.y - size / 2, size, size * 0.8, 2, {
              fill: content.chunk,
              stroke: 'ink',
              strokeWidth: STROKE.detail,
            }),
          ],
          { transform: { rotate: position.turn, ox: position.x, oy: position.y } },
        );
      });
    const gloss = shape(
      path()
        .move(BOWL.cx - rx * 0.5, surfaceY - ry * 0.25)
        .quad(BOWL.cx - rx * 0.1, surfaceY - ry * 0.55, BOWL.cx + rx * 0.25, surfaceY - ry * 0.35)
        .toString(),
      { stroke: 'paper', strokeWidth: STROKE.detail },
    );
    const molten = contents(bowlVessel, LEVEL, content.melted, [gloss]);

    const angle = t * 3 * Math.PI * 2;
    const spatulaNode = place(
      BOWL.cx + Math.cos(angle) * rx * 0.35,
      surfaceY + 2 + Math.sin(angle) * 3,
      -18 + Math.cos(angle) * 8,
      spatula(wave(t, 3) * 0.5),
      0.78,
    );
    const current = [faded(melt, [molten]), ...chunkNodes(melt)];
    return compact([
      ...counter(),
      ...burner.back,
      ...potVessel.back,
      ...bowlVessel.back,
      // Gleiche Gruppenstruktur wie faded(1, …), damit die Schleife exakt schließt.
      reset > 0 ? group(chunkNodes(0)) : null,
      faded(1 - reset, current),
      spatulaNode,
      ...bowlVessel.front,
      ...potVessel.front,
      ...burner.front,
      ...steam(POT.cx - POT.width / 2 - 2, POT_RIM - 2, t, { count: 1, height: 22, strength: 0.7 }),
      ...steam(POT.cx + POT.width / 2 + 2, POT_RIM - 2, t + 0.5, { count: 1, height: 22, strength: 0.7 }),
      stoveKnob(KNOB_X, POWER),
    ]);
  },
  hints() {
    return [arcArrow(BOWL.cx, BOWL.rim - 2, 40, 10, 200, 340)];
  },
});
